import { map } from "lodash";
import { Chart } from "chart.js/auto";
import "chartjs-adapter-date-fns";

const charts = {};

export interface PieChartData {
  label: string;
  value: number;
  color: string;
}

function getChart(elementId: string, defaultInfo: any): Chart | null {
  let chart: Chart = charts[elementId];

  if (chart && !chart.canvas)
    return null;

  if (!chart || !chart.canvas.isConnected) {
    chart = charts[elementId] = new Chart(
      document.getElementById(elementId) as HTMLCanvasElement,
      defaultInfo,
    );
  }
  return chart;
}

const pieChartDefaults = {
  type: "doughnut",
  data: {
    datasets: [],
    labels: [],
  },
  options: {
    cutoutPercentage: 40,
    responsive: true,
    animation: {
      duration: 0,
    },
    plugins: {
      datalabels: {
        display: false,
      },
      legend: {
        display: false,
        position: "right",
        labels: {
          usePointStyle: true,
        },
      },
    },
  },
};

export function displayPieChart(elementId: string, data: Array<PieChartData>) {
  const chart = getChart(elementId, { ...pieChartDefaults });

  if (!chart)
    return;

  chart.data = {
    datasets: [
      {
        data: map(data, (d) => d.value),
        backgroundColor: map(data, (d) => d.color),
      },
    ],
    labels: map(data, (d) => d.label),
  };

  chart.update();
}

export interface BarChartData {
  labels: string[];
  datasets: BarChartDataSet[];
}

export interface BarChartDataSet {
  data: number[];
  label: string;
  backgroundColor: string;
}

const stackedBarChartDefaults = {
  type: "bar",
  data: [],
  options: {
    scaleStartValue: 0,
    tooltips: {
      mode: "index",
      intersect: false,
    },
    responsive: true,
    scales: {
      xAxes: [
        {
          stacked: true,
        },
      ],
      yAxes: [
        {
          stacked: true,
          ticks: {
            beginAtZero: true,
          },
        },
      ],
    },
  },
};

export function displayStackedBarChart(
  elementId: string,
  labels: string[],
  datasets: BarChartDataSet[],
) {
  const chart = getChart(elementId, { ...stackedBarChartDefaults });

  if (!chart)
    return;

  chart.data = {
    datasets: datasets,
    labels: labels,
  };
  chart.update();
}

const barChartDefaults = {
  type: "bar",
  data: [],
  options: {
    scaleStartValue: 0,
    tooltips: {
      mode: "index",
      intersect: false,
    },
    legend: {
      display: false,
      position: "right",
    },
    responsive: true,
    scales: {
      yAxes: [
        {
          ticks: {
            beginAtZero: true,
          },
        },
      ],
    },
  },
};

export function displayBarChart(elementId: string, labels: string[], dataset: BarChartDataSet) {
  const chart = getChart(elementId, { ...barChartDefaults });

  if (!chart)
    return;

  chart.data = {
    datasets: [dataset],
    labels: labels,
  };
  chart.update();
}

export interface LineChartDataSet {
  data: number[];
  label: string;
  borderColor: string;
  backgroundColor: string;
  fill?: boolean;
  tension?: number;
  pointRadius?: number;
  pointBackgroundColor?: string | string[];
}

const lineChartDefaults = {
  type: "line",
  data: [],
  options: {
    responsive: true,
    plugins: {
      legend: {
        display: true,
        position: "top",
      },
    },
    scales: {
      y: {
        beginAtZero: true,
      },
    },
  },
};

export function displayLineChart(
  elementId: string,
  labels: string[],
  datasets: LineChartDataSet[],
) {
  const chart = getChart(elementId, { ...lineChartDefaults });

  if (!chart)
    return;

  chart.data = {
    datasets: datasets,
    labels: labels,
  };
  chart.update();
}

export interface TimeLineChartDataPoint {
  x: Date;
  y: number;
}

export interface TimeLineChartDataSet {
  data: TimeLineChartDataPoint[];
  label: string;
  borderColor: string;
  backgroundColor: string;
  fill?: boolean;
  tension?: number;
  pointRadius?: number;
  pointBackgroundColor?: string | string[];
  showLine?: boolean;
}

const timeLineChartDefaults = {
  type: "line",
  data: [],
  options: {
    responsive: true,
    plugins: {
      legend: {
        display: true,
        position: "top",
      },
    },
    scales: {
      x: {
        type: "time",
        time: {
          displayFormats: {
            minute: "M/d h:mm a",
            hour: "M/d h a",
            day: "M/d",
            week: "M/d",
            month: "MMM yyyy",
          },
          tooltipFormat: "MM/dd/yyyy, h:mm:ss a",
        },
        ticks: {
          maxRotation: 45,
          minRotation: 45,
          maxTicksLimit: 8,
        },
        title: {
          display: true,
          text: "Date/Time",
        },
      },
      y: {
        beginAtZero: true,
        title: {
          display: true,
          text: "Duration (s)",
        },
      },
    },
  },
};

export function displayTimeLineChart(
  elementId: string,
  datasets: TimeLineChartDataSet[],
) {
  const chart = getChart(elementId, { ...timeLineChartDefaults });

  if (!chart)
    return;

  chart.data = {
    datasets: datasets as any,
  };
  chart.update();
}
